/**
 * Erros da API.
 *
 * Um único envelope para toda resposta de erro, com código estável. O cliente
 * decide o que fazer pelo `code`; a `message` é texto para o usuário e pode
 * mudar sem quebrar ninguém (API 1, SERVER 8).
 */
import { z } from "zod";
/** Lista estável de códigos de erro (API 1). */
export const ERROR_CODES = [
    "VALIDATION",
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
    "EMAIL_TAKEN",
    "INVALID_CREDENTIALS",
    "EMAIL_NOT_VERIFIED",
    "TOKEN_EXPIRED",
    "ENTITLEMENT_REQUIRED",
    "LEAGUE_FULL",
    "LEAGUE_STARTED",
    "DEADLINE_PASSED",
    "ROUND_PROCESSING",
    "NOT_MEMBER",
    "NOT_COMMISSIONER",
    "INVALID_LINEUP",
    "WINDOW_CLOSED",
    "INSUFFICIENT_FUNDS",
    "OFFER_BELOW_MINIMUM",
    "TRADE_COOLDOWN",
    "PLAYER_UNAVAILABLE",
    "RATE_LIMITED",
    "ENGINE_VERSION_MISMATCH",
    // Genéricos, para o que não merece código próprio. `CONFLICT` cobre "isso já
    // existe" e "outra pessoa mexeu antes"; `PAYLOAD_TOO_LARGE` cobre arquivo
    // acima do limite. Sem eles, os dois casos caíam em `VALIDATION`, e o cliente
    // não tinha como distinguir "corrija o formulário" de "escolha outro arquivo".
    "CONFLICT",
    "PAYLOAD_TOO_LARGE",
    // Falta de configuracao do servidor, nao caso de negocio: a rota de compra
    // existe e recusa porque ninguem cadastrou credencial de provedor. Sem codigo
    // proprio isso virava 500, e 500 pede para o cliente tentar de novo.
    "BILLING_NOT_CONFIGURED",
    // Outra conta ligada a esta ja esta na liga (SECURITY 5). Merece codigo
    // proprio porque a tela precisa dizer outra coisa: nao e liga cheia, nao e
    // conflito de estado qualquer, e a pessoa tem como contestar.
    "LINKED_ACCOUNT",
    // Convocacao fora do ano de Copa das Nacoes (API 17). Nao cabe em
    // `WINDOW_CLOSED`, que e a janela de transferencias: a tela que o cliente
    // precisa abrir e outra, e a resposta carrega em que temporada a Copa volta,
    // que e a unica informacao util para quem tentou convocar cedo demais.
    "NATIONS_WINDOW_CLOSED",
    // Chave do alfa fechado ausente, inexistente ou ja usada. Nao cabe em
    // `VALIDATION`, que e "corrija o formulario": o formato pode estar perfeito e
    // a chave continuar sem valer, porque outra pessoa a usou primeiro. A tela
    // precisa dizer isso no campo da chave e so nele.
    "ALPHA_KEY_INVALID",
    // A conta ja ativou aparelhos demais nos ultimos trinta dias. Nao e
    // `ENTITLEMENT_REQUIRED`: a pessoa comprou, e comprar de novo nao resolve. A
    // tela precisa dizer que o limite e de aparelhos e que ele volta sozinho.
    "DEVICE_LIMIT",
    // Segundo fator do painel. Três códigos porque a tela faz três coisas
    // diferentes: mandar cadastrar o app, pedir o código de agora, ou dizer que
    // o código digitado não valeu.
    "TOTP_SETUP_REQUIRED",
    "TOTP_REQUIRED",
    "TOTP_INVALID",
    "EMAIL_NOT_CONFIGURED",
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/** Conteúdo do erro, sem o envelope. */
export const ApiErrorSchema = z.object({
    code: ErrorCodeSchema,
    /** Texto em português do Brasil, pronto para exibição. */
    message: z.string().min(1),
    /** Dados extras específicos do código, como `{ max: 20 }` em `LEAGUE_FULL`. */
    details: z.record(z.unknown()).optional(),
});
/** Corpo devolvido por qualquer rota que falha. */
export const ApiErrorBodySchema = z.object({ error: ApiErrorSchema });
//# sourceMappingURL=errors.js.map